import type { PageBlock, PageElementData, PageSection } from './schema';

/**
 * EAA-2063: blocks the learner never sees. The BE stores a generated item's audio script
 * (`block-transcript-*`) and speaking/essay rubric hints (`block-analysis-*`) as page blocks
 * with `settings.hidden: true`, because the graders read the page text. Players and previews
 * skip them; only the page editor shows them.
 */
export function isPageBlockHidden(block: Pick<PageBlock, 'settings'> | null | undefined): boolean {
    return block?.settings?.hidden === true;
}

export function visiblePageBlocks(section: Pick<PageSection, 'blocks'>): PageBlock[] {
    return (section.blocks ?? []).filter((b) => !isPageBlockHidden(b));
}

/** A section that had blocks, all hidden, renders nothing (not even its title). */
export function isPageSectionEmptyForLearner(
    section: Pick<PageSection, 'blocks' | 'settings'>
): boolean {
    if (section.settings?.hidden) return true;
    const blocks = section.blocks ?? [];
    return blocks.length > 0 && visiblePageBlocks(section).length === 0;
}

/** Whether a page has anything the learner would see (for players deciding to show a panel). */
export function hasVisiblePageContent(
    page: Pick<PageElementData, 'sections'> | null | undefined
): boolean {
    return (page?.sections ?? []).some(
        (s) => !s.settings?.hidden && visiblePageBlocks(s).length > 0
    );
}
